/**
 * Open PDFs as a top-level blob: URL in a new tab.
 *
 * Chrome's built-in PDF viewer wraps an <iframe src="blob:..."> in a data:text/html
 * document with an opaque origin, then blocks that document from loading the blob
 * ("Not allowed to load local resource: blob:https://…"). Do not put blob URLs in
 * iframes or data:text/html wrappers.
 */

const BLOB_REVOKE_MS = 60_000;

function normalizePdfBlob(blob: Blob): Blob {
  const t = (blob.type || '').toLowerCase();
  if (t === 'application/pdf') return blob;
  return new Blob([blob], { type: 'application/pdf' });
}

/** Preserve API content-type; only infer MIME from filename when type is missing/generic. */
function blobForFileDownload(blob: Blob, filename: string): Blob {
  const t = (blob.type || '').toLowerCase();
  if (t && t !== 'application/octet-stream') {
    return blob;
  }
  const ext = filename.includes('.') ? filename.split('.').pop()!.toLowerCase() : '';
  const mime =
    ext === 'csv'
      ? 'text/csv;charset=utf-8'
      : ext === 'pdf'
        ? 'application/pdf'
        : ext === 'xlsx'
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : undefined;
  return mime ? new Blob([blob], { type: mime }) : blob;
}

function scheduleRevoke(objectUrl: string, revokeAfterMs: number): void {
  window.setTimeout(() => {
    try {
      URL.revokeObjectURL(objectUrl);
    } catch {
      /* ignore */
    }
  }, revokeAfterMs);
}

function navigateExistingTab(tab: Window, url: string): boolean {
  try {
    tab.location.replace(url);
    return true;
  } catch {
    return false;
  }
}

/**
 * Open a blank tab during the user gesture so a later blob navigation is not popup-blocked.
 * Do not pass `noopener` here — the caller needs the Window handle.
 */
export function openBlankTargetedTab(): Window | null {
  const tab = window.open('about:blank', '_blank');
  if (!tab) {
    return null;
  }
  try {
    tab.opener = null;
  } catch {
    /* ignore */
  }
  return tab;
}

export function navigateTabToBlob(
  tab: Window | null,
  blob: Blob,
  revokeAfterMs: number = BLOB_REVOKE_MS
): boolean {
  const pdfBlob = normalizePdfBlob(blob);
  const objectUrl = URL.createObjectURL(pdfBlob);

  let opened = false;
  if (tab && !tab.closed) {
    opened = navigateExistingTab(tab, objectUrl);
  }
  if (!opened) {
    opened = !!window.open(objectUrl, '_blank', 'noopener');
  }

  if (!opened) {
    URL.revokeObjectURL(objectUrl);
    return false;
  }

  scheduleRevoke(objectUrl, revokeAfterMs);
  return true;
}

export function navigateTabToUrl(tab: Window | null, url: string): boolean {
  if (tab && !tab.closed && navigateExistingTab(tab, url)) {
    return true;
  }
  return !!window.open(url, '_blank', 'noopener');
}

export function closeTabIfOpen(tab: Window | null): void {
  try {
    if (tab && !tab.closed) {
      tab.close();
    }
  } catch {
    // ignore
  }
}

export function downloadBlobAsFile(blob: Blob, filename: string): void {
  const objectUrl = URL.createObjectURL(blobForFileDownload(blob, filename));
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  scheduleRevoke(objectUrl, BLOB_REVOKE_MS);
}
